import { DEFAULT_COUNTRY_TLD, DOMAIN_COUNTRY } from '@features/cities/constants'
import type { City, CountryTld, FormattedCity } from '@features/cities/types'
import ruLayoutConvert from 'convert-layout'
import CyrillicToTranslit from 'cyrillic-to-translit-js'

const transliterator = CyrillicToTranslit()

/** Возвращает поддерживаемый TLD либо TLD по умолчанию. */
export const resolveCountryTld = (hostnameTld: string): CountryTld => {
  if (hostnameTld in DOMAIN_COUNTRY) {
    return hostnameTld as CountryTld
  }

  return DEFAULT_COUNTRY_TLD
}

/** Возвращает название страны для TLD. */
export const getCountryNameByTld = (
  tld: string | undefined,
): string | undefined => {
  if (!tld) {
    return undefined
  }

  return DOMAIN_COUNTRY[resolveCountryTld(tld)]
}

/** Возвращает новый список городов, отсортированный по отображаемому имени. */
export const sortCitiesByVisibleName = (cities: City[]): City[] =>
  [...cities].sort((a, b) => a.visibleName.localeCompare(b.visibleName, 'ru'))

/** Фильтрует города по точному названию страны. */
export const filterCitiesByCountry = (
  cities: City[],
  countryName: string | undefined,
): City[] => {
  if (!countryName) {
    return []
  }

  return cities.filter((city) => city.country.name === countryName)
}

/** Добавляет городу абсолютный URL. */
export const withCityUrl = (city: City): FormattedCity => ({
  ...city,
  cityUrl: `https://${city.domain}`,
})

/** Добавляет маркеры первой страны и первой буквы для группировки списка. */
export const setCountryAndLetter = (cities: City[]): FormattedCity[] => {
  const countryLetters: Record<string, Set<string>> = {}

  return cities.map((city) => {
    const firstCountry = city.country.name
    const firstLetter = city.visibleName.at(0) ?? ''
    const cityWithUrl = withCityUrl(city)
    const letters = countryLetters[firstCountry]

    if (letters) {
      if (letters.has(firstLetter)) {
        return cityWithUrl
      }

      letters.add(firstLetter)
      return { ...cityWithUrl, firstLetter }
    }

    countryLetters[firstCountry] = new Set([firstLetter])

    return { ...cityWithUrl, firstLetter, firstCountry }
  })
}

/** Возвращает вариант поискового запроса, совпавший с названием города. */
export const getMatchedCityQuery = (
  cityName: string,
  query: string,
): string | undefined => {
  const normalizedName = cityName.toLowerCase()
  const normalizedQuery = query.toLowerCase().trim()

  if (!normalizedQuery) return undefined

  return [
    normalizedQuery,
    ruLayoutConvert.ru.fromEn(normalizedQuery),
    transliterator.reverse(normalizedQuery),
  ].find((variant) => normalizedName.includes(variant))
}

/** Фильтрует города по запросу с учётом раскладки и транслитерации. */
export const filterCitiesByQuery = (cities: City[], query: string): City[] => {
  const searchQueryString = query.toLowerCase().trim()

  if (!searchQueryString) {
    return cities
  }

  return cities.filter((city) =>
    Boolean(getMatchedCityQuery(city.visibleName, searchQueryString)),
  )
}

/** Формирует итоговый список городов для отображения и поиска. */
export const formatCitiesList = ({
  cities,
  countryName,
  searchQuery,
}: {
  cities: City[]
  countryName: string | undefined
  searchQuery: string
}): FormattedCity[] => {
  const sorted = sortCitiesByVisibleName(cities)
  const byCountry = filterCitiesByCountry(sorted, countryName)

  if (!searchQuery) {
    return setCountryAndLetter(byCountry)
  }

  return setCountryAndLetter(filterCitiesByQuery(byCountry, searchQuery))
}
